// case-studies/pages/jfs.js
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import "../styles/jfs.css";

import example1 from "../assets/jfs/jfsbanner.JPG";
import example2 from "../assets/jfs/sportscards.jpg";
import example3 from "../assets/jfs/ecomm.png";
import example4 from "../assets/jfs/thepivot.PNG";
import example5 from "../assets/jfs/rolex.JPG";

const titleVariant = {
  hidden: { opacity: 0, y: -28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const textVariant = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const imageVariant = {
  hidden: { opacity: 0, scale: 1.18 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const JFS = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, []);

  return (
    <main className="jfs-root">
      {/* Top title and meta */}
      <header className="jfs-header">
        <div className="jfs-header-inner">
          <div className="jfs-title-block">
            <motion.h1
              className="jfs-title"
              variants={titleVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              The Football Shirt Business I Built by Accident
            </motion.h1>
            <motion.p
              className="jfs-subtitle"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              From childhood obsession to $5,000 monthly recurring revenue, from
              thrift finds to a Rolex purchase, this is the story of how I built
              the UAE’s only football shirt restoration and customization hub.
            </motion.p>
          </div>

          <div className="jfs-meta-row">
            <motion.div
              className="jfs-meta-line"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            />
            <motion.div
              className="jfs-meta-items"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <div className="jfs-meta-item">
                <span className="jfs-meta-label">Project Type</span>
                <span className="jfs-meta-value">
                  Passion brand and service business
                </span>
              </div>
              <div className="jfs-meta-item">
                <span className="jfs-meta-label">Role</span>
                <span className="jfs-meta-value">Founder and operator</span>
              </div>
              <div className="jfs-meta-item">
                <span className="jfs-meta-label">Years</span>
                <span className="jfs-meta-value">2018 – Present</span>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Banner image */}
      <section className="jfs-banner">
        <div className="jfs-banner-inner">
          <motion.img
            src={example1}
            alt="Johns Football Shirts visual"
            className="jfs-banner-image"
            variants={imageVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          />

          <motion.a
            href="https://www.instagram.com/johnsfootballshirts/"
            className="jfs-view-instagram-link"
            target="_blank"
            rel="noopener noreferrer"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            VIEW INSTAGRAM
          </motion.a>
        </div>
      </section>

      {/* The Hook */}
      <section className="jfs-section jfs-section-full">
        <div className="jfs-section-inner">
          <motion.h2
            className="jfs-section-title jfs-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            The Hook
          </motion.h2>
          <motion.div
            className="jfs-section-body jfs-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              I grew up watching football like most boys my age. But the night
              my cousin showed me Chelsea playing,{" "}
              <span className="jfs-emphasis-inline">everything clicked</span>. I
              saw Didier Drogba running through defenders like a man possessed
              and I knew right then that this game was for me.
            </p>
            <p>
              Since that night,{" "}
              <span className="jfs-emphasis-inline">
                football shirts became my thing
              </span>
              . Not the cheap knockoffs. The real stuff. I collected every
              Chelsea home shirt from 1996 to now. Over sixty Chelsea shirts in
              total. Over{" "}
              <span className="jfs-emphasis-inline">six hundred shirts</span>{" "}
              passing through my hands over the years. A collection that later
              turned into a business, then a brand, then something bigger than I
              ever expected.
            </p>
            <p>
              Johns Football Shirts.{" "}
              <span className="jfs-emphasis-inline">
                The only restoration and customization hub of its kind in the
                entire UAE.
              </span>
            </p>
            <p>
              But it didn’t start that way. It didn’t even start as a business.
              It started with me just wanting to own things.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Context – title, image, text */}
      <section className="jfs-section jfs-section-split jfs-split-text-left">
        <div className="jfs-section-inner jfs-section-inner-split">
          <motion.h3
            className="jfs-section-title jfs-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            Context
          </motion.h3>

          <div className="jfs-split-col jfs-split-col-media">
            <div className="jfs-image-frame">
              <motion.img
                src={example2}
                alt="Early shirt collecting and posting"
                className="jfs-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="jfs-split-col jfs-split-col-text">
            <motion.div
              className="jfs-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                Growing up, I liked collecting anything. Trading cards were
                first. Football shirts came later, when my allowance got higher
                and I could afford better things. By the mid 2010s I had around
                ten shirts. Nothing serious. Mostly outlet finds and gifts.
              </p>
              <p>
                Then the 2020 pandemic hit. Everything shut down. No work. No
                money flowing. No real world at all. And that’s when I stumbled
                into thrifting.
              </p>
              <p>
                Online thrift shops were untouched treasure chests. I was
                finding incredible shirts for insane prices. I bought a Germany
                1994. I bought a squad-signed Chelsea shirt. I even picked up
                signed memorabilia because these sellers had no clue what they
                had listed.
              </p>
              <p>
                My collection shot up to around{" "}
                <span className="jfs-emphasis-inline">forty shirts</span> in a
                few months.
              </p>
              <p>
                And since 2018, I had been posting on this tiny Instagram
                account I made called{" "}
                <span className="jfs-emphasis-inline">
                  @johnsfootballshirts
                </span>
                . Amateur photos, detailed captions, and a bit of personality.
                That page eventually{" "}
                <span className="jfs-emphasis-inline">
                  became my identity
                </span>
                .
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Challenge – title, image, text */}
      <section className="jfs-section jfs-section-split jfs-split-text-right">
        <div className="jfs-section-inner jfs-section-inner-split">
          <motion.h3
            className="jfs-section-title jfs-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            The Challenge
          </motion.h3>

          <div className="jfs-split-col jfs-split-col-media">
            <div className="jfs-image-frame">
              <motion.img
                src={example3}
                alt="Thrifting and Depop hustle"
                className="jfs-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="jfs-split-col jfs-split-col-text">
            <motion.div
              className="jfs-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                When everything closed for quarantine, I had to figure out a{" "}
                <span className="jfs-emphasis-inline">way to make money</span>.
                I had shirts, I had knowledge, and I had an audience. So I{" "}
                <span className="jfs-emphasis-inline">
                  opened an online shop, found a logistics partner, and created
                  a small e-commerce brand around it
                </span>
                .
              </p>
              <p>
                To my surprise, it actually worked. I was clearing around{" "}
                <span className="jfs-emphasis-inline">
                  four hundred dollars a month
                </span>
                . Not crazy money, but for a side hustle built out of boredom, I
                wasn’t complaining.
              </p>
              <p>
                I bought shirts in bulk from Poland and Ukraine and sold them to
                my Dubai audience. Demand was solid. But then{" "}
                <span className="jfs-emphasis-inline">
                  something strange showed up in my spreadsheet
                </span>
                .
              </p>
              <p>
                <span className="jfs-emphasis-inline">
                  Shirts with namesets sold way better
                </span>{" "}
                than shirts without players on the back. It didn’t matter if the
                player was world class or from a mid-table team. People
                connected to names. Stores did not offer customization. And
                there was my first real moment of clarity.
              </p>
              <p>
                If stores won’t put names on shirts,{" "}
                <span className="jfs-emphasis-inline">I will</span>.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Strategy */}
      <section className="jfs-section jfs-section-full">
        <div className="jfs-section-inner">
          <motion.h2
            className="jfs-section-title jfs-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Strategy
          </motion.h2>
          <motion.div
            className="jfs-section-body jfs-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              That simple idea turned into years of sourcing suppliers from
              China, Hong Kong, the UK, and Spain until I built a network of
              partners who trust me, prioritize me, and deliver fast. It took
              trial and error. It took risking money when I barely had any. But
              I kept going.
            </p>
            <p>
              Within a few years, I became the only person in the UAE who could
              reliably access authentic namesets and patches{" "}
              <span className="jfs-emphasis-inline">
                across different eras and leagues
              </span>
              . No big chain stores. No sports retailers. Just me.
            </p>
            <p>
              But thrifting eventually{" "}
              <span className="jfs-emphasis-inline">got saturated</span>. Too
              many sellers with{" "}
              <span className="jfs-emphasis-inline">deeper pockets</span> jumped
              in and flipped shirts harder than I could. My identity was always
              affordable shirts with your favorite players. But I was getting
              priced out.
            </p>
            <p>
              That’s when my brother stepped in with the line that changed
              everything.
            </p>
            <p>
              <span className="jfs-emphasis-inline">
                “Why buy shirts and sell them? When people already have shirts
                at home that need fixing?”
              </span>
            </p>
            <p>And again, something clicked.</p>
          </motion.div>
        </div>
      </section>

      {/* The Pivot – title, image, text */}
      <section className="jfs-section jfs-section-split jfs-split-text-left">
        <div className="jfs-section-inner jfs-section-inner-split">
          <motion.h3
            className="jfs-section-title jfs-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            The Pivot
          </motion.h3>

          <div className="jfs-split-col jfs-split-col-media">
            <div className="jfs-image-frame">
              <motion.img
                src={example4}
                alt="Restoration and customization workflow"
                className="jfs-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="jfs-split-col jfs-split-col-text">
            <motion.div
              className="jfs-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                I transformed @johnsfootballshirts from a reseller page into
                something new. A place where people bring their old shirts to
                get restored, cleaned up, revived, customized, or turned into
                something personal. I also added arbitrage services so people
                could get shirts shipped fast without tracking foreign sellers
                themselves.
              </p>
              <p>
                This{" "}
                <span className="jfs-emphasis-inline">
                  pivot, was everything
                </span>
                .
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Outcome – title, image, text */}
      <section className="jfs-section jfs-section-split jfs-split-text-right">
        <div className="jfs-section-inner jfs-section-inner-split">
          <motion.h3
            className="jfs-section-title jfs-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            The Outcome
          </motion.h3>

          <div className="jfs-split-col jfs-split-col-media">
            <div className="jfs-image-frame">
              <motion.img
                src={example5}
                alt="Clients and network"
                className="jfs-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="jfs-split-col jfs-split-col-text">
            <motion.div
              className="jfs-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                After the switch, things exploded. I started getting{" "}
                <span className="jfs-emphasis-inline">
                  clients every month
                </span>
                . On my{" "}
                <span className="jfs-emphasis-inline">
                  best months I pulled in over five thousand dollars
                </span>
                . I worked with{" "}
                <span className="jfs-emphasis-inline">
                  royalty from Sharjah and Umm Al Quwain
                </span>
                . Media reps from Al Jazeera Sports. Local brands. Podcasts.
                Collectors with insane private stashes. And I met football
                legends like Luis Figo, Francesco Totti, Gary McAllister, and
                Zlatan Ibrahimovic.
              </p>
              <p>
                <span className="jfs-emphasis-inline">
                  A side hustle turned into a network of connections I never
                  imagined I’d have.
                </span>
              </p>
              <p>
                It gave me stability. It gave me freedom. It let me earn from
                something I genuinely love doing. And when I had one of my
                biggest months ever,{" "}
                <span className="jfs-emphasis-inline">
                  I bought a Rolex to celebrate
                </span>
                . A small win for some people, but for me it was a reminder of
                how far this idea had come.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reflection */}
      <section className="jfs-section jfs-section-full jfs-section-reflection">
        <div className="jfs-section-inner">
          <motion.h2
            className="jfs-section-title jfs-section-title-left"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Reflection
          </motion.h2>
          <motion.div
            className="jfs-section-body jfs-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              This whole thing started with being obsessed with football shirts.
              Then it became a business. Then it became a brand. Then it became
              a{" "}
              <span className="jfs-emphasis-inline">
                service no one else in the UAE offered
              </span>
              .
            </p>
            <p>
              Johns Football Shirts is the definition of building something out
              of passion and surviving long enough to pivot when the world
              changes. It’s messy, personal, and built the same way I collected
              shirts when I was a kid. One piece at a time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing visual (optional) */}
      {/* <section className="jfs-section jfs-closing-visual">
        <div className="jfs-section-inner">
          <div className="jfs-image-frame jfs-image-frame-soft">
            <motion.img
              src={example6}
              alt="Johns Football Shirts detail"
              className="jfs-image"
              variants={imageVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            />
          </div>
        </div>
      </section> */}
    </main>
  );
};

export default JFS;
